import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import useContent from "../../../hooks/useContent";
import EditCard from "./EditCard";
import { findsOnce } from "./edits";
import { Composer, Dot, EdgeTab, LABEL, Panel, ThinkingLabel } from "./style";

const LINK_BUTTON = {
  ...LABEL,
  color: "text.primary",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
};

// Whole seconds since the component mounted. Counted off a start timestamp
// rather than by incrementing, so a throttled background tab resumes at the
// real elapsed time instead of resuming its count where it left off.
const useElapsed = () => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const id = setInterval(
      () => setSeconds(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => clearInterval(id);
  }, []);
  return seconds;
};

/**
 * The wait. Mounted only while a request is in flight, so the elapsed count
 * starts from zero on its own and needs no reset.
 *
 * The count is hidden from the log's aria-live region: it changes every second,
 * and a screen reader reading "thinking 1, thinking 2…" over the wait would
 * bury the reply when it lands. The word itself is announced once.
 */
const Thinking = ({ text }) => {
  const seconds = useElapsed();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        {[0, 1, 2].map((i) => (
          <Dot key={i} style={{ animationDelay: `${i * 160}ms` }} />
        ))}
      </Box>
      <ThinkingLabel component="span">{text.thinking}</ThinkingLabel>
      {seconds ? (
        <Box
          component="span"
          aria-hidden
          sx={{ ...LABEL, color: "text.disabled" }}
        >
          {text.elapsed.replace("{{seconds}}", seconds)}
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * Chat panel docked beside the editor, always open — it is a column of the
 * editor's own layout, not a drawer over the page.
 *
 * The conversation lives in local state only. Nothing is persisted, so the
 * thread starts fresh whenever the editor is reopened and New chat is a reset
 * of that state rather than anything the server has to hear about. Applying an
 * edit hands it back to the page via onApplyEdits, which mutates the editor
 * buffer; the existing Save button is still what writes anything to storage.
 */
export default function AssistantPanel({
  contentId,
  markdown,
  selection,
  onClearSelection,
  onApplyEdits,
  text,
  uiLanguage,
}) {
  const theme = useTheme();
  // Two columns stop fitting well above the app's own 768px mobile break, so
  // this collapses at md rather than following useMediaQuery.
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(false);
  const { assistArticle } = useContent();
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // "<turn>:<edit>" for every edit the user has already applied or rejected.
  const [resolved, setResolved] = useState({});
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [turns, busy]);

  const resolve = (keys) =>
    setResolved((prev) => ({
      ...prev,
      ...keys.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    }));

  const pending = (turnIndex, edits) =>
    edits
      .map((edit, editIndex) => ({ edit, key: `${turnIndex}:${editIndex}` }))
      .filter(({ key }) => !resolved[key]);

  const applyMany = (entries) => {
    const applied = onApplyEdits(entries.map(({ edit }) => edit));
    const landed = new Set(applied);
    resolve(entries.filter(({ edit }) => landed.has(edit)).map(({ key }) => key));
  };

  const send = async () => {
    const message = input.trim();
    if (!message || busy) return;
    const usedSelection = selection || undefined;
    const history = turns
      .filter((turn) => !turn.error)
      .map(({ role, content }) => ({ role, content }));

    setTurns((prev) => [
      ...prev,
      { role: "user", content: message, selection: usedSelection },
    ]);
    setInput("");
    setBusy(true);
    try {
      const result = await assistArticle(contentId, {
        markdown,
        message,
        selection: usedSelection,
        uiLanguage,
        history,
      });
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result.reply,
          edits: result.edits || [],
          dropped: result.dropped || 0,
        },
      ]);
      onClearSelection();
    } catch (error) {
      setTurns((prev) => [
        ...prev,
        { role: "assistant", error: error?.message || text.failed },
      ]);
    } finally {
      setBusy(false);
    }
  };

  // Held disabled while a request is in flight: a reply landing in a thread the
  // user just cleared would read as an answer to nothing.
  const startNewChat = () => {
    setTurns([]);
    setResolved({});
    setInput("");
    onClearSelection();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const empty = !turns.length && !busy;

  if (compact && !expanded)
    return (
      <Tooltip title={text.title} arrow>
        <EdgeTab onClick={() => setExpanded(true)} aria-label={text.title}>
          <AutoAwesomeRoundedIcon fontSize="small" />
        </EdgeTab>
      </Tooltip>
    );

  return (
    <Panel className={compact ? "floating" : ""}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }}
      >
        <AutoAwesomeRoundedIcon
          fontSize="small"
          sx={{ color: "text.secondary" }}
        />
        {/* Chrome furniture, not content: dragging across it only ever produced
            a stray highlight next to the article the user is actually editing. */}
        <Box sx={{ flex: 1, minWidth: 0, userSelect: "none" }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
            {text.title}
          </Typography>
        </Box>
        <Tooltip title={text.newChat} arrow>
          <span>
            <IconButton
              size="small"
              onClick={startNewChat}
              disabled={busy || (empty && !input)}
              aria-label={text.newChat}
            >
              <RestartAltRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {compact ? (
          <Tooltip title={text.close} arrow>
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              aria-label={text.close}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Divider />

      <Box
        role="log"
        aria-live="polite"
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
          p: 2,
        }}
      >
        {empty ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {text.empty}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {text.suggestions.map((suggestion) => (
                <Chip
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  clickable
                  label={suggestion}
                  onClick={() => {
                    setInput(suggestion);
                    inputRef.current?.focus();
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : null}

        {turns.map((turn, turnIndex) =>
          turn.role === "user" ? (
            <Box key={turnIndex} sx={{ alignSelf: "flex-end", maxWidth: "88%" }}>
              {turn.selection ? (
                <Typography
                  dir="auto"
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    color: "text.disabled",
                    textAlign: "end",
                    fontStyle: "italic",
                  }}
                >
                  “{turn.selection.slice(0, 90)}
                  {turn.selection.length > 90 ? "…" : ""}”
                </Typography>
              ) : null}
              <Box
                dir="auto"
                sx={{
                  bgcolor: "background.dark",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {turn.content}
              </Box>
            </Box>
          ) : (
            <Box
              key={turnIndex}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                // A faint change bar: the assistant's prose is marginalia on the
                // article. Its proposals below carry the heavy bar (ChangeCard),
                // which separates the two levels without leaning on colour.
                borderInlineStart: "2px solid",
                borderColor: "borderColor.dark",
                paddingInlineStart: 1.5,
              }}
            >
              <Typography
                dir="auto"
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  color: turn.error ? "error.main" : "text.primary",
                }}
              >
                {turn.error || turn.content}
              </Typography>

              {turn.edits?.length ? (
                <>
                  <Typography sx={LABEL}>
                    {turn.edits.length === 1
                      ? text.oneChange
                      : text.changes.replace("{{count}}", turn.edits.length)}
                  </Typography>
                  {turn.edits.map((edit, editIndex) => {
                    const key = `${turnIndex}:${editIndex}`;
                    if (resolved[key]) return null;
                    return (
                      <EditCard
                        key={key}
                        edit={edit}
                        index={editIndex}
                        stale={!findsOnce(markdown, edit.find)}
                        text={text}
                        onApply={() => applyMany([{ edit, key }])}
                        onReject={() => resolve([key])}
                      />
                    );
                  })}
                  {pending(turnIndex, turn.edits).length > 1 ? (
                    <Box sx={{ display: "flex", gap: 2, pt: 0.25 }}>
                      <Typography
                        component="button"
                        onClick={() => applyMany(pending(turnIndex, turn.edits))}
                        sx={LINK_BUTTON}
                      >
                        {text.applyAll}
                      </Typography>
                      <Typography
                        component="button"
                        onClick={() =>
                          resolve(
                            pending(turnIndex, turn.edits).map(({ key }) => key)
                          )
                        }
                        sx={{ ...LINK_BUTTON, color: "text.secondary" }}
                      >
                        {text.rejectAll}
                      </Typography>
                    </Box>
                  ) : null}
                </>
              ) : null}

              {turn.dropped ? (
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  {text.dropped.replace("{{count}}", turn.dropped)}
                </Typography>
              ) : null}
            </Box>
          )
        )}

        {busy ? <Thinking text={text} /> : null}
        <Box ref={bottomRef} />
      </Box>

      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {selection ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<FormatQuoteRoundedIcon fontSize="small" />}
            label={
              selection.length > 40 ? `${selection.slice(0, 40)}…` : selection
            }
            onDelete={onClearSelection}
            sx={{ alignSelf: "flex-start", maxWidth: "100%" }}
          />
        ) : null}
        <Composer>
          <InputBase
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={6}
            value={input}
            disabled={busy}
            placeholder={text.placeholder}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: "0.875rem", lineHeight: 1.6, py: 0.5 }}
            inputProps={{ dir: "auto", "aria-label": text.placeholder }}
          />
          <Tooltip title={text.send} arrow>
            <span>
              <IconButton
                size="small"
                onClick={send}
                disabled={busy || !input.trim()}
                aria-label={text.send}
                sx={{
                  bgcolor: "text.primary",
                  color: "background.default",
                  "&:hover": { bgcolor: "text.secondary" },
                  "&.Mui-disabled": { bgcolor: "background.dark" },
                }}
              >
                <ArrowUpwardRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Composer>
        {input ? (
          <Typography
            sx={{ ...LABEL, textTransform: "none", color: "text.disabled" }}
          >
            {text.hint}
          </Typography>
        ) : null}
      </Box>
    </Panel>
  );
}
